// Serverless share links: the whole comparison state is compressed into the URL
// FRAGMENT (never sent to any server — fragments aren't transmitted in requests).
// Practical fragment budget is ~8–10 KB; past that we tell the user to export.
import LZString from 'lz-string';

const { compressToEncodedURIComponent, decompressFromEncodedURIComponent } = LZString;

const PREFIX = 'd='; // fragment payload marker
export const MAX_FRAGMENT = 9000; // chars — conservative URL budget

/** @returns {{ ok:true, fragment, url } | { ok:false, reason, size }} */
export function encodeState(state) {
  if (state.format === 'parquet' || state.leftDocument || state.rightDocument) return { ok: false, reason: 'binary-input', size: 0 };
  const json = JSON.stringify(pick(state));
  const packed = compressToEncodedURIComponent(json);
  const fragment = PREFIX + packed;
  if (fragment.length > MAX_FRAGMENT) {
    return { ok: false, reason: 'too-large', size: fragment.length };
  }
  const base = typeof location !== 'undefined' ? location.origin + location.pathname : '';
  return { ok: true, fragment, url: `${base}#${fragment}` };
}

/** Parse a location.hash back into state, or null if absent/invalid. */
export function decodeState(hash) {
  if (!hash) return null;
  const raw = hash.replace(/^#/, '');
  if (!raw.startsWith(PREFIX)) return null;
  // The cap used to apply only when MAKING a link. A fragment is attacker-
  // authored, lz-string expands ~1000x (5.6 KB decodes to 5 MB), and its worst
  // case grows with the square of the input, so a 2 MB URL could hang the tab
  // before any shape check ran — and again on every reload (REL-093). Every link
  // this site has ever produced fits the cap, so nothing legitimate is refused.
  if (raw.length > MAX_FRAGMENT) return null;
  try {
    const json = decompressFromEncodedURIComponent(raw.slice(PREFIX.length));
    if (!json) return null;
    return sanitize(JSON.parse(json));
  } catch {
    return null;
  }
}

// A fragment is attacker-authorable: anyone can hand a victim a #d= link. The
// parsed payload is therefore coerced to the exact shape pick() produces before
// it reaches applyState(), so an unexpected type (object where a string is
// expected, a non-object `options`) can never desynchronise the UI (REL-054).
const SHARE_FORMATS = new Set(['text', 'json', 'yaml', 'csv', 'tsv']);
const str = (v) => (typeof v === 'string' ? v : v == null ? '' : String(v));

function sanitize(parsed) {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const rawOpts = parsed.options;
  const src = rawOpts && typeof rawOpts === 'object' && !Array.isArray(rawOpts) ? rawOpts : {};
  // Object.create(null) so a literal `__proto__` key in the fragment stays inert data.
  const options = Object.create(null);
  for (const flag of ['ignoreWhitespace', 'ignoreCase', 'ignoreKeyOrder']) {
    if (src[flag] !== undefined) options[flag] = !!src[flag];
  }
  if (src.ignoreFields !== undefined) {
    options.ignoreFields = Array.isArray(src.ignoreFields)
      ? src.ignoreFields.map(str)
      : str(src.ignoreFields).split(',').map((f) => f.trim()).filter(Boolean);
  }
  return {
    // Only a format the input can actually hold. setFormat already collapses an
    // unknown string to text, but the state should not carry one at all.
    format: SHARE_FORMATS.has(parsed.format) ? parsed.format : undefined,
    left: str(parsed.left),
    right: str(parsed.right),
    keyColumn: typeof parsed.keyColumn === 'string' ? parsed.keyColumn : null,
    options,
  };
}

function pick(s) {
  return {
    format: s.format,
    left: s.left,
    right: s.right,
    keyColumn: s.keyColumn ?? null,
    options: s.options ?? {},
  };
}
